import React from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { RouteSegmentResponse } from '../../models';
import { useLocaleContext } from '../../i18n';

interface Props {
  segments: RouteSegmentResponse[];
}

export function buildElevationData(segments: RouteSegmentResponse[]) {
  const raw: { km: number; elevation: number }[] = [{ km: 0, elevation: 0 }];
  let elevation = 0;
  for (const seg of segments) {
    elevation += (seg.gradientPercent / 100) * seg.distanceKm * 1000;
    raw.push({ km: parseFloat(seg.endKm.toFixed(2)), elevation });
  }
  const min = Math.min(...raw.map(p => p.elevation));
  return raw.map(p => ({ ...p, elevation: Math.round(p.elevation - min) }));
}

export default function RouteElevationChart({ segments }: Props) {
  const { locale } = useLocaleContext();
  const data = buildElevationData(segments);

  return (
    <div style={{ width: '100%', height: 220 }}>
      <ResponsiveContainer>
        <AreaChart data={data} margin={{ top: 10, right: 10, left: 10, bottom: 24 }}>
          <defs>
            <linearGradient id="elevGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#198754" stopOpacity={0.4} />
              <stop offset="95%" stopColor="#198754" stopOpacity={0.05} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#444" />
          <XAxis
            dataKey="km"
            type="number"
            domain={['dataMin', 'dataMax']}
            tickFormatter={(v) => v.toFixed(1)}
            label={{ value: locale.planner.gpx.chartLabel, position: 'insideBottom', offset: -12, fill: '#adb5bd' }}
            tick={{ fill: '#adb5bd', fontSize: 11 }}
            stroke="#555"
          />
          <YAxis
            tickFormatter={(v) => `${v}`}
            tick={{ fill: '#adb5bd', fontSize: 11 }}
            stroke="#555"
            width={45}
          />
          <Tooltip
            contentStyle={{ backgroundColor: '#2b3035', border: '1px solid #495057', color: '#f8f9fa' }}
            formatter={(v: number) => [`${v} m`, locale.planner.gpx.elevationLabel]}
            labelFormatter={(km) => `${Number(km).toFixed(1)} km`}
          />
          <Area
            type="monotone"
            dataKey="elevation"
            stroke="#198754"
            strokeWidth={2}
            fill="url(#elevGradient)"
            dot={false}
            activeDot={{ r: 4, fill: '#198754' }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
